import React from 'react';
import { Box, TextField, Button, Autocomplete, Typography, Divider, Grid } from 'components/mui';
import { useActivityLogForm } from 'containers/pages/activityLogManagement/hooks';
import { ActivityLog, ActivityLogFormData } from 'containers/pages/activityLogManagement/types';

interface ActivityLogFormProps {
  mode: 'add' | 'edit';
  activityLog?: ActivityLog | null;
  onSave: (formData: ActivityLogFormData, isEdit: boolean) => Promise<void>;
}

function ActivityLogForm({ mode, activityLog, onSave }: ActivityLogFormProps) {
  const { state, handler } = useActivityLogForm({ mode, activityLog, onSave });
  const form = state.form;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* General Info */}
      <Typography variant="subtitle2" fontWeight="bold">General Info</Typography>
      <TextField label="Title *" name="Title" value={form.Title} onChange={handler.onForm}
        error={state.valid.Title.isErr} helperText={state.valid.Title.errMsg} fullWidth size="small" />
      <TextField label="Description" name="Description" value={form.Description} onChange={handler.onForm}
        multiline minRows={4} fullWidth size="small" />
      <TextField label="Status Comment" name="StatusComment" value={form.StatusComment} onChange={handler.onForm}
        multiline minRows={2} fullWidth size="small" />
      <Autocomplete
        options={state.statusOptions}
        getOptionLabel={(opt) => opt.Title || ''}
        value={state.statusOptions.find((o) => o.Id === form.StatusId) || (form.StatusId ? { Id: form.StatusId, Title: form.StatusTitle } : null)}
        onChange={handler.onStatusChange}
        onInputChange={handler.onStatusSearch}
        isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
        onOpen={() => handler.onStatusSearch(null, '')}
        renderInput={(params) => <TextField {...params} label="Status *" error={state.valid.StatusId.isErr}
          helperText={state.valid.StatusId.errMsg} size="small" />}
      />

      <Divider />

      {/* Contacts */}
      <Typography variant="subtitle2" fontWeight="bold">Contacts</Typography>
      <Autocomplete
        options={state.customerOptions}
        getOptionLabel={(opt) => opt.Name || ''}
        value={state.customerOptions.find((o) => o.Id === form.CustomerId) || (form.CustomerId ? { Id: form.CustomerId, Name: form.CustomerName } : null)}
        onChange={handler.onCustomerChange}
        onInputChange={handler.onCustomerSearch}
        isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
        onOpen={() => handler.onCustomerSearch(null, '')}
        renderInput={(params) => <TextField {...params} label="Customer *" error={state.valid.CustomerId.isErr}
          helperText={state.valid.CustomerId.errMsg} size="small" />}
      />
      <Autocomplete
        options={state.consultantOptions}
        getOptionLabel={(opt) => opt.FullName || ''}
        value={state.consultantOptions.find((o) => o.Id === form.ConsultantId) || (form.ConsultantId ? { Id: form.ConsultantId, FullName: form.ConsultantName } : null)}
        onChange={handler.onConsultantChange}
        onInputChange={handler.onConsultantSearch}
        isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
        onOpen={() => handler.onConsultantSearch(null, '')}
        renderInput={(params) => <TextField {...params} label="Consultant *" error={state.valid.ConsultantId.isErr}
          helperText={state.valid.ConsultantId.errMsg} size="small" />}
      />
      <Autocomplete
        options={state.contactOptions}
        getOptionLabel={(opt) => opt.FullName || ''}
        value={state.contactOptions.find((o) => o.Id === form.PrimaryContactId) || (form.PrimaryContactId ? { Id: form.PrimaryContactId, FullName: form.PrimaryContactName } : null)}
        onChange={handler.onContactChange}
        onInputChange={handler.onContactSearch}
        isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
        onOpen={() => handler.onContactSearch(null, '')}
        disabled={!form.CustomerId}
        renderInput={(params) => <TextField {...params} label="Primary Contact"
          helperText={!form.CustomerId ? 'Select a customer first' : ''} size="small" />}
      />

      <Divider />

      {/* GPD */}
      <Typography variant="subtitle2" fontWeight="bold">Group / Project / Deliverable</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            options={state.groupOptions}
            getOptionLabel={(opt) => opt.Title || ''}
            value={state.groupOptions.find((o) => o.Id === form.GroupId) || (form.GroupId ? { Id: form.GroupId, Title: form.GroupTitle } : null)}
            onChange={handler.onGroupChange}
            onInputChange={handler.onGroupSearch}
            isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
            onOpen={() => handler.onGroupSearch(null, '')}
            disabled={!form.CustomerId}
            renderInput={(params) => <TextField {...params} label="Group"
              helperText={!form.CustomerId ? 'Select a customer first' : ''} size="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            options={state.projectOptions}
            getOptionLabel={(opt) => opt.Title || ''}
            value={state.projectOptions.find((o) => o.Id === form.ProjectId) || (form.ProjectId ? { Id: form.ProjectId, Title: form.ProjectTitle } : null)}
            onChange={handler.onProjectChange}
            onInputChange={handler.onProjectSearch}
            isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
            onOpen={() => handler.onProjectSearch(null, '')}
            disabled={!form.GroupId}
            renderInput={(params) => <TextField {...params} label="Project"
              helperText={!form.GroupId ? 'Select a group first' : ''} size="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            options={state.deliverableOptions}
            getOptionLabel={(opt) => opt.Title || ''}
            value={state.deliverableOptions.find((o) => o.Id === form.DeliverableId) || (form.DeliverableId ? { Id: form.DeliverableId, Title: form.DeliverableTitle } : null)}
            onChange={handler.onDeliverableChange}
            onInputChange={handler.onDeliverableSearch}
            isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
            onOpen={() => handler.onDeliverableSearch(null, '')}
            disabled={!form.ProjectId}
            renderInput={(params) => <TextField {...params} label="Deliverable"
              helperText={!form.ProjectId ? 'Select a project first' : ''} size="small" />}
          />
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
        <Button variant="contained" disabled={state.canNotSave} onClick={handler.onSave}>
          {mode === 'edit' ? 'SAVE' : 'CREATE'}
        </Button>
        <Button variant="outlined" onClick={handler.onReset}>
          RESET
        </Button>
      </Box>
    </Box>
  );
}

export default ActivityLogForm;
